import React, { memo, useState } from "react";
import Icon from "components/Elements/Icon/Icon";
import dayjs from "dayjs";
import parse from "html-react-parser";
import usePageHeightApplyForBanner from "./hooks/usePageHeightApplyForBanner";
import calculateLinkRenewablePage from "helpers/calculateLinkRenewablePage.ts";
import useSubscriptionExpiration from "hooks/useSubscriptionExpiration";

import { useContent } from "contexts/contentContext/contentContext";
import { useUser } from "contexts/userContext/userContext";
import { ButtonA } from "components/Elements/Button/Button";
import type { BillingDetailsT, UserDataT } from "models/models";

import styles from "./subscriptionExpirationReminder.module.scss";

const SHOW_REMINDER_KEY = "showSubscriptionExpirationReminder";
const MAX_HIDDEN_TIME_HOURS = 24;
const GRACE_PERIOD_DAYS = 14;
const EXPIRATION_ALERT_DAYS = 14;

interface SubscriptionExpirationReminderI
  extends React.HTMLAttributes<HTMLDivElement> {
  showMegaMenuSection: string;
}

const SubscriptionExpirationReminder = memo(
  (props: SubscriptionExpirationReminderI) => {
    const { getTextByKey, getPath } = useContent();
    const { getUserData, getUserBillingDetails, isSubscribed } = useUser();

    const user = getUserData() as UserDataT;
    const userExists = Object.keys(user).length > 0;

    const userBillingDetails = getUserBillingDetails() as BillingDetailsT;
    const isAutoRenew = userBillingDetails?.auto_renew;

    const { expirationDate, gracePeriod, currDayIsExpirationDay } =
      useSubscriptionExpiration();

    const subscriptionExpirationDate = new Date(user.Subscribtion_Expired);

    const today = new Date();
    const diffTime = Math.abs(
      subscriptionExpirationDate.getTime() - today.getTime()
    );
    const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Get the current difference in minutes between local time and UTC
    const localOffsetInMinutes = new Date().getTimezoneOffset();

    // Convert the difference to milliseconds for accurate calculations
    const offsetInMilliseconds = localOffsetInMinutes * 60 * 1000;

    // Correct the subscription expiration time by adjusting for local timezone offset
    const correctedSubscriptionExpirationDate = new Date(
      subscriptionExpirationDate.getTime() + offsetInMilliseconds
    );

    const isExpired =
      today.getTime() > correctedSubscriptionExpirationDate.getTime();

    // Subscription Expiration date is less than 14 days away (Subscription expires within 14 days)
    const isExpirationDateShown =
      isSubscribed() &&
      !user.IsSubUser &&
      !isAutoRenew &&
      ((daysLeft <= EXPIRATION_ALERT_DAYS && daysLeft > 0) ||
        currDayIsExpirationDay);

    // User is sub user
    const isOrgSubscriptionText =
      userExists && user.IsSubUser
        ? gracePeriod?.daysLeft !== undefined &&
          gracePeriod.daysLeft > 0 &&
          gracePeriod.daysLeft <= GRACE_PERIOD_DAYS &&
          isExpired
          ? getTextByKey("org-subscription-expired-text")
          : daysLeft <= EXPIRATION_ALERT_DAYS && !currDayIsExpirationDay
          ? getTextByKey("org-subscription-expiration-text")
          : currDayIsExpirationDay && !isExpired
          ? getTextByKey("org-subscription-expires-today-text")
          : null
        : null;

    const lastShownDate = localStorage.getItem(SHOW_REMINDER_KEY);

    const [isReminderAllowedToBeShown, setIsReminderAllowedToBeShown] =
      useState(
        lastShownDate
          ? dayjs().diff(dayjs(lastShownDate), "hours") > MAX_HIDDEN_TIME_HOURS
          : true
      );

    const handleClose = () => {
      localStorage.setItem(SHOW_REMINDER_KEY, dayjs().format());
      setIsReminderAllowedToBeShown(false);
    };

    const isHidden =
      // userBillingDetails?.status === "ACTIVE" ||
      !expirationDate ||
      !isReminderAllowedToBeShown ||
      isAutoRenew ||
      (!isExpirationDateShown &&
        !gracePeriod?.daysLeft &&
        !isOrgSubscriptionText) ||
      userBillingDetails.cancelType === "cancel-immediately";

    usePageHeightApplyForBanner(isHidden, props.showMegaMenuSection);

    if (isHidden) {
      return null;
    }

    const gracePeriodRenewLink = calculateLinkRenewablePage(
      getUserBillingDetails()?.period,
      user.EmbedSubscription,
      user.Subscription,
      getPath()
    );

    return (
      <div
        {...props}
        className={styles.wrapper}
        id="subscription-expiration-reminder"
      >
        <Icon name="red-alert" ext="png" iconClass={styles.alertIcon} />
        {isExpirationDateShown ? (
          <div>
            <p className={styles.reminderText}>
              {currDayIsExpirationDay && !isExpired
                ? getTextByKey("Subscription-expires-today")
                : parse(
                    getTextByKey("reminder-subscription-expiration-text")
                      .replace("#days#", daysLeft)
                      .replace("#date#", expirationDate)
                  )}
            </p>
            <p className={styles.explanation}>
              {getTextByKey("subscription-expiration-alert")}
            </p>
          </div>
        ) : null}
        {!user.IsSubUser && gracePeriod?.daysLeft && isExpired ? (
          <div>
            <p>
              {getTextByKey("renew-subscription-reminder-title")}{" "}
              <span className={styles.boldText}>{expirationDate}</span>
            </p>
            {gracePeriod?.daysLeft === 1 ? (
              <p className={styles.explanation}>
                {getTextByKey("grace-period-expires-today")}
              </p>
            ) : (
              <p className={styles.explanation}>
                {getTextByKey("subscription-in-grace-alert").replace(
                  "#days#",
                  gracePeriod.daysLeft
                )}
              </p>
            )}
          </div>
        ) : null}
        {isOrgSubscriptionText ? (
          <div>
            <p>{isOrgSubscriptionText}</p>
          </div>
        ) : null}
        {!isOrgSubscriptionText &&
        isExpirationDateShown &&
        !isExpired &&
        user.subUserRole !== "Admin" &&
        user.subUserRole !== "Supervisor" ? (
          //@ts-ignore
          <ButtonA
            buttonClass={styles.renewButton}
            page={"user/profile/account"}
          >
            {getTextByKey("renew-old-subscripition-button")}
          </ButtonA>
        ) : null}
        {!isOrgSubscriptionText &&
        gracePeriod?.daysLeft &&
        !isExpirationDateShown &&
        user.subUserRole !== "Admin" &&
        user.subUserRole !== "Supervisor" ? (
          //@ts-ignore
          <ButtonA buttonClass={styles.renewButton} href={gracePeriodRenewLink}>
            {getTextByKey("renew-old-subscripition-button")}
          </ButtonA>
        ) : null}
        <Icon
          id="reminder-close-icon"
          name="x"
          onClick={handleClose}
          gray
          iconClass={styles.closeIcon}
        />
      </div>
    );
  }
);

export default SubscriptionExpirationReminder;
